import { useState } from "react";

import { type ApiError, toApiError } from "../../api/client";
import { deleteAudit, listAudits } from "../../api/freightAudit";
import { RunStatusBadge } from "../../components/Badges";
import { Button } from "../../components/Button";
import { buttonClass } from "../../components/buttonClass";
import { Dialog } from "../../components/Drawer";
import { EmptyState, ErrorNotice, Skeleton } from "../../components/Feedback";
import { useAsync } from "../../hooks/useAsync";
import { Link } from "../../components/Link";
import { formatCount, formatDate, formatDateTime, formatMoney } from "../../lib/format";
import type { AuditRun } from "../../types/api";

const PAGE_SIZE = 20;

function ConfirmDelete({ run, onCancel, onDeleted }: { run: AuditRun; onCancel: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function confirm() {
    setBusy(true);
    try {
      await deleteAudit(run.id);
      onDeleted();
    } catch (caught) {
      setError(toApiError(caught));
      setBusy(false);
    }
  }

  return (
    <Dialog onClose={onCancel} labelledBy="delete-title">
      <h2 id="delete-title">Delete audit {run.reference}?</h2>
      <p className="muted">Its results and every review decision will be removed. This can&apos;t be undone.</p>
      {error ? <ErrorNotice error={error} compact /> : null}
      <div className="dialog__actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="danger" icon="trash" onClick={confirm} busy={busy}>
          Delete audit
        </Button>
      </div>
    </Dialog>
  );
}

export function AuditHistory() {
  const [offset, setOffset] = useState(0);
  const audits = useAsync((signal) => listAudits({ limit: PAGE_SIZE, offset }, signal), String(offset));
  const [deleting, setDeleting] = useState<AuditRun | null>(null);
  const page = audits.data;

  return (
    <section className="history" aria-labelledby="history-heading">
      <div className="step-heading step-heading--row">
        <div>
          <h1 id="history-heading">Audit history</h1>
          <p className="muted">Audits run from this browser. Anyone with an audit&apos;s link can open it.</p>
        </div>
        <Link to="/" className={buttonClass("primary")}>
          New audit
        </Link>
      </div>

      {audits.error ? <ErrorNotice error={audits.error} onRetry={audits.reload} /> : null}
      {!page && audits.loading ? <Skeleton lines={5} label="Loading audits" /> : null}
      {page && page.total === 0 ? (
        <EmptyState
          title="No audits yet."
          action={
            <Link to="/" className={buttonClass("primary")}>
              Start your first audit
            </Link>
          }
        >
          <p>Upload a shipment ledger and a carrier invoice export to get your first exception queue.</p>
        </EmptyState>
      ) : null}

      {page && page.total > 0 ? (
        <div className="panel table-wrap">
          <table className="history-table">
            <caption className="visually-hidden">Audit history</caption>
            <thead>
              <tr>
                <th scope="col">Audit</th>
                <th scope="col">Files</th>
                <th scope="col" className="numeric">
                  Invoices
                </th>
                <th scope="col">Result</th>
                <th scope="col" className="numeric">
                  Variance flagged
                </th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((run) => (
                <tr key={run.id}>
                  <td>
                    <Link to={`/audits/${run.id}`} className="history-table__ref">
                      {run.reference}
                    </Link>
                    <span className="muted small">{formatDateTime(run.created_at)}</span>
                  </td>
                  <td className="history-table__files">
                    <span>{run.shipment_filename ?? "API request"}</span>
                    <span className="muted">{run.invoice_filename ?? "API request"}</span>
                  </td>
                  <td className="numeric">{formatCount(run.invoice_rows)}</td>
                  <td>
                    {run.summary ? (
                      <span className="result-counts">
                        <span className="count count--pass">{run.summary.pass} pass</span>
                        <span className="count count--review">{run.summary.review} review</span>
                        <span className="count count--hold">{run.summary.hold} hold</span>
                      </span>
                    ) : (
                      <RunStatusBadge status={run.status} />
                    )}
                  </td>
                  <td className="numeric">{run.summary ? formatMoney(run.summary.potential_positive_variance) : "—"}</td>
                  <td className="history-table__actions">
                    {run.expires_at ? <span className="muted small">Until {formatDate(run.expires_at)}</span> : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="trash"
                      aria-label={`Delete audit ${run.reference}`}
                      onClick={() => setDeleting(run)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && page.total > PAGE_SIZE ? (
        <nav className="pager" aria-label="Pages">
          <span>
            {formatCount(offset + 1)}–{formatCount(Math.min(offset + PAGE_SIZE, page.total))} of {formatCount(page.total)}
          </span>
          <Button size="sm" icon="chevron-left" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Newer
          </Button>
          <Button size="sm" iconAfter="chevron-right" disabled={offset + PAGE_SIZE >= page.total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Older
          </Button>
        </nav>
      ) : null}

      {deleting ? (
        <ConfirmDelete
          run={deleting}
          onCancel={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            audits.reload();
          }}
        />
      ) : null}
    </section>
  );
}
